import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { Message } from '../types/chat';

interface ChatMessageProps {
  message: Message;
  isStreaming?: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, isStreaming = false }) => {
  const isUser = message.role === 'user';

  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: '1rem',
      padding: '0 1rem'
    }}>
      <div style={{
        maxWidth: '70%',
        padding: '12px 16px',
        borderRadius: '18px',
        backgroundColor: isUser ? '#E85D04' : '#f1f3f5',
        color: isUser ? 'white' : '#333'
      }}>
        <div style={{
          fontSize: '14px',
          lineHeight: '1.4'
        }}>
          {isUser ? (
            // Messages utilisateur en texte simple
            <div style={{ whiteSpace: 'pre-wrap' }}>
              {message.content}
            </div>
          ) : (
            // Messages assistant avec markdown
            <div style={{
              color: 'inherit',
              overflow: 'hidden'
            }}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
                components={{
                  // Personnaliser les composants markdown
                  p: ({children}: any) => <p style={{ margin: '0.5em 0', wordBreak: 'break-word' }}>{children}</p>,
                  code: ({children, className, ...props}: any) => {
                    const inline = !className;
                    return inline ? (
                      <code
                        style={{
                          backgroundColor: '#f3f4f6',
                          padding: '2px 4px',
                          borderRadius: '3px',
                          fontSize: '0.9em',
                          fontFamily: 'Monaco, Consolas, monospace'
                        }}
                        {...props}
                      >
                        {children}
                      </code>
                    ) : (
                      <pre style={{
                        backgroundColor: '#f8f9fa',
                        padding: '12px',
                        borderRadius: '6px',
                        overflow: 'auto',
                        margin: '0.5em 0',
                        fontSize: '13px',
                        fontFamily: 'Monaco, Consolas, monospace'
                      }}>
                        <code className={className} {...props}>{children}</code>
                      </pre>
                    );
                  },
                  ul: ({children}: any) => <ul style={{ marginLeft: '1em', marginBottom: '0.5em' }}>{children}</ul>,
                  ol: ({children}: any) => <ol style={{ marginLeft: '1em', marginBottom: '0.5em' }}>{children}</ol>,
                  li: ({children}: any) => <li style={{ marginBottom: '0.25em' }}>{children}</li>,
                  blockquote: ({children}: any) => (
                    <blockquote style={{
                      borderLeft: '4px solid #e5e7eb',
                      paddingLeft: '1em',
                      margin: '0.5em 0',
                      fontStyle: 'italic',
                      color: '#6b7280'
                    }}>
                      {children}
                    </blockquote>
                  ),
                  h1: ({children}: any) => <h1 style={{ fontSize: '1.25em', fontWeight: 'bold', margin: '0.5em 0' }}>{children}</h1>,
                  h2: ({children}: any) => <h2 style={{ fontSize: '1.15em', fontWeight: 'bold', margin: '0.5em 0' }}>{children}</h2>,
                  h3: ({children}: any) => <h3 style={{ fontSize: '1.1em', fontWeight: 'bold', margin: '0.5em 0' }}>{children}</h3>,
                  table: ({children}: any) => (
                    <table style={{
                      borderCollapse: 'collapse',
                      width: '100%',
                      margin: '0.5em 0',
                      fontSize: '0.9em'
                    }}>
                      {children}
                    </table>
                  ),
                  th: ({children}: any) => (
                    <th style={{
                      border: '1px solid #d1d5db',
                      padding: '8px',
                      backgroundColor: '#f9fafb',
                      fontWeight: 'bold',
                      textAlign: 'left'
                    }}>
                      {children}
                    </th>
                  ),
                  td: ({children}: any) => (
                    <td style={{
                      border: '1px solid #d1d5db',
                      padding: '8px'
                    }}>
                      {children}
                    </td>
                  )
                }}
              >
                {message.content}
              </ReactMarkdown>
              {isStreaming && (
                <span style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '14px',
                  backgroundColor: '#333',
                  marginLeft: '2px',
                  animation: 'blink 1s infinite'
                }}>|</span>
              )}
            </div>
          )}
        </div>
        <div style={{
          fontSize: '11px',
          marginTop: '4px',
          opacity: 0.7
        }}>
          {message.timestamp.toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;